import { forwardRef, useState } from 'react';
import clsx from 'clsx';
import { type FormattedSuggestion } from 'hooks/useGoogleAutocomplete';
import { FOCUSABLE_TAB_INDEX, NON_FOCUSABLE_TAB_INDEX } from 'utils/constants';
import styles from './StreetInput.module.scss';

interface RenderSuggestionsProps {
  data: FormattedSuggestion[];
  handleClick: (item: google.maps.places.AutocompleteSuggestion) => void;
  suggestionsListId: string;
  isOpened: boolean;
  optionsActive: boolean;
}

const Suggestions = forwardRef<HTMLUListElement, RenderSuggestionsProps>((props, ref) => {
  const { data, handleClick, suggestionsListId, isOpened, optionsActive } = props;
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const onClickHandler = (original: FormattedSuggestion['original'], placeId: string) => () => {
    setSelectedOption(placeId);
    handleClick(original);
  };

  return (
    <div className={clsx(styles.listContainer, isOpened && styles.open)}>
      {!!data.length && isOpened && (
        <ul ref={ref} role="listbox" id={suggestionsListId}>
          {data.map((item, idx) => {
            const { mainText, secondaryText, original } = item;
            const liKey = `${original.placePrediction?.placeId}-${idx}`;
            const ariaSelected = selectedOption === original.placePrediction?.placeId;

            return (
              <li
                key={liKey}
                className={styles.option}
                role="option"
                aria-selected={ariaSelected}
                tabIndex={optionsActive ? FOCUSABLE_TAB_INDEX : NON_FOCUSABLE_TAB_INDEX}
                onMouseDown={(e) => e.preventDefault()}
                onMouseUp={onClickHandler(original, item.placeId)}
              >
                <span className={styles.mainText}>{mainText}</span>
                {secondaryText && <span className={styles.secondaryText}>, {secondaryText}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
});

export default Suggestions;
